import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { appPagePath } from '../devmap';
import './usage/usage.css';

/**
 * Every app you can manage, with its three scores, in one table.
 *
 * The OS-Evals tab answers "how is this one". Nothing answered "how are they
 * all", so comparing meant opening a tab per app and holding the numbers in
 * your head — which is exactly when a 5/5 that should have been a 4/5 goes
 * unnoticed.
 *
 * Scores come from the same endpoint logic as the per-app tab, so the two
 * cannot disagree. Nothing here triggers a model review: a list view must not
 * spend money, so an app nobody has reviewed reads "?" rather than a number.
 */
const SCORE_COLUMNS = [
  { key: 'name', label: 'Application', align: 'left' },
  { key: 'value', label: 'Societal value add' },
  { key: 'readiness', label: 'Readiness' },
  { key: 'usage', label: 'Paradigm feature usage' },
  { key: 'checks', label: 'Checks' },
];

/* What the graph says, rather than what the DLR intends. Reach is this app
 * linking its data to something it does not own; pull is another app linking to
 * what this one wrote. Two directions, two different facts. */
const CONNECTION_COLUMNS = [
  { key: 'name', label: 'Application', align: 'left' },
  { key: 'edges', label: 'Edges it created', title: 'Measured from the graph, not from what the app asks for' },
  { key: 'reach', label: 'Reaches outward', title: 'Edges this app drew to nodes outside its own schemas. None at all can be the right answer.' },
  { key: 'pull', label: 'Built on by others', title: 'Edges another app drew to nodes this one wrote' },
  { key: 'shape', label: 'Shape', align: 'left' },
];

function Level({ score, unit = '/5' }) {
  if (!score) return <span className="utable__muted" title="No CRUX, so nothing is evaluated">—</span>;
  if (score.assessed === false) {
    return (
      <span className="utable__unscored" title="No review has run for this app yet">
        ?<em>{unit}</em>
      </span>
    );
  }
  return (
    <span className={`utable__level is-${score.level}`}>
      <strong>{score.level}</strong><em>{unit}</em>
      <i>{score.name}</i>
    </span>
  );
}

function FleetScores({ groupId, mode = 'scores' }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ by: mode === 'connections' ? 'pull' : 'readiness', dir: 'desc' });
  const columns = mode === 'connections' ? CONNECTION_COLUMNS : SCORE_COLUMNS;

  useEffect(() => {
    let cancelled = false;
    const path = groupId
      ? `/third-party/my-apps/scores?group_id=${groupId}`
      : '/third-party/my-apps/scores';
    api.request(path)
      .then(r => { if (!cancelled) setRows(r?.apps || []); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not load scores.'); });
    return () => { cancelled = true; };
  }, [groupId]);

  const sorted = useMemo(() => {
    if (!rows) return [];
    const dir = sort.dir === 'asc' ? 1 : -1;
    // An unscored app sorts last whichever way the column points: "nobody has
    // judged it" is not a low score, and letting it read as one is the mistake
    // this table exists to stop making.
    const rank = (r) => {
      if (sort.by === 'name') return null;
      if (['edges', 'reach', 'pull'].includes(sort.by)) {
        const c = r.connectedness;
        if (!c) return null;
        return sort.by === 'edges' ? c.edges_created : c[sort.by];
      }
      if (sort.by === 'shape') return null;
      const s = r[sort.by];
      if (!s) return null;
      if (sort.by === 'checks') return s.total ? s.passed / s.total : null;
      return s.assessed === false ? null : s.level;
    };
    return [...rows].sort((a, b) => {
      if (sort.by === 'name') return dir * String(a.name).localeCompare(String(b.name));
      const av = rank(a); const bv = rank(b);
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return dir * (av - bv);
    });
  }, [rows, sort]);

  const click = (key) => setSort(s => ({
    by: key,
    dir: s.by === key ? (s.dir === 'asc' ? 'desc' : 'asc') : (key === 'name' ? 'asc' : 'desc'),
  }));

  if (error) return <p className="usage__msg is-bad">{error}</p>;
  if (!rows) return <p className="usage__msg">Loading…</p>;
  if (!rows.length) return <p className="usage__msg">No apps yet.</p>;

  // Evals open the app's OS-Evals page; connections open the app itself.
  const open = (id) => navigate(appPagePath(id, mode === 'connections' ? 'overview' : 'readiness'));

  // What the prose used to explain rides the column labels as tooltips.
  return (
    <section className="usage">
      <div className="utable-wrap">
        <table className="utable">
          <thead>
            <tr>
              {columns.map(c => (
                <th key={c.key} className={c.align === 'left' ? 'is-left' : ''}>
                  <button onClick={() => click(c.key)} title={c.title} className={sort.by === c.key ? 'is-active' : ''}>
                    {c.label}{sort.by === c.key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map(r => (
              <tr key={r.id} className="is-link" onClick={() => open(r.id)} tabIndex={0}
                  onKeyDown={e => e.key === 'Enter' && open(r.id)}>
                <td className="is-left">
                  <span className="utable__name">{r.name}</span>
                  <span className="utable__sub">
                    v{r.spec_version}
                    {r.deployment_mode === 'incubator' && <> · incubator</>}
                    {r.is_plugin && <> · plugin</>}
                  </span>
                </td>
                {mode === 'connections' ? (
                  r.connectedness ? (
                    <>
                      <td>{r.connectedness.edges_created.toLocaleString()}</td>
                      <td>{r.connectedness.reach.toLocaleString()}</td>
                      <td>{r.connectedness.pull.toLocaleString()}</td>
                      <td className="is-left">
                        <span className={`utable__shape is-${r.connectedness.shape}`}>
                          {r.connectedness.shape}
                        </span>
                        <span className="utable__note">{r.connectedness.what}</span>
                      </td>
                    </>
                  ) : (
                    <td colSpan={4} className="utable__blocked">Not measured</td>
                  )
                ) : r.blocked ? (
                  <td colSpan={4} className="utable__blocked">
                    No CRUX yet — nothing is evaluated until one is written
                  </td>
                ) : (
                  <>
                    <td><Level score={r.value} /></td>
                    <td><Level score={r.readiness} /></td>
                    <td>
                      <Level score={r.usage} />
                      <span className="utable__note">{r.usage.using} of {r.usage.of} surfaces</span>
                    </td>
                    <td>
                      <strong>{r.checks.passed}</strong><span className="utable__muted">/{r.checks.total}</span>
                      {r.checks.not_applicable > 0 && (
                        <em className="utable__warn" title="Checks that could not run — a skipped check is not a passed one">
                          +{r.checks.not_applicable} n/a
                        </em>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default FleetScores;
